import { DEFAULT_LOCALE, isLocale } from "./locales";
import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import en from "../locales/ui.en.json";
import ko from "../locales/ui.ko.json";
import ru from "../locales/ui.ru.json";

const BUNDLES = { en, ru, ko };

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const { lang: rawLang } = useParams();
  const lang = isLocale(rawLang) ? rawLang : DEFAULT_LOCALE;
  const navigate = useNavigate();
  const location = useLocation();

  const setLang = useCallback(
    (next) => {
      if (!isLocale(next)) return;
      try {
        window.localStorage.setItem("lang", next);
      } catch (_) {
        /* ignore */
      }
      const segments = location.pathname.split("/");
      if (isLocale(segments[1])) {
        segments[1] = next;
      } else {
        segments.splice(1, 0, next);
      }
      // replace:true keeps the back button useful (language toggle shouldn't
      // accumulate history); preserving search + hash keeps the user's
      // section anchor intact when they switch languages mid-page.
      navigate(segments.join("/") + location.search + location.hash, {
        replace: true,
      });
    },
    [navigate, location.pathname, location.search, location.hash]
  );

  const value = useMemo(
    () => ({ lang, setLang, bundles: BUNDLES }),
    [lang, setLang]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}

LocaleProvider.propTypes = { children: PropTypes.node };

export { LocaleContext };
